import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import { getPageContent } from '../services/pageContentService';
import { SITE_NAME } from '../config/site';
import './Navbar.css';

const DEFAULT_LOGO = '/logo512.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoSrc, setLogoSrc] = useState(DEFAULT_LOGO);
  const [logoAlt, setLogoAlt] = useState(SITE_NAME);
  const location = useLocation();
  const { t } = useLanguage();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const closeMenu = () => setIsOpen(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const data = await getPageContent('site', 'logo');
        if (!isMounted || !data) return;
        if (data.src?.trim()) setLogoSrc(data.src.trim());
        if (data.alt?.trim()) setLogoAlt(data.alt.trim());
      } catch {
        // keep defaults
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const brandShort = SITE_NAME.replace(' Dental Care', '');

  return (
    <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`} aria-label="Main navigation">
      <div className="navbar-container">
        <Link to="/" className="logo-link" aria-label={`${SITE_NAME} home`}>
          <img src={logoSrc} alt={logoAlt} className="logo-img" />
          <span className="logo-text">{brandShort}</span>
        </Link>

        <ul id="main-nav-links" className={`nav-links ${isOpen ? 'active' : ''}`}>
          <li><Link to="/" className={isActive('/') && location.pathname === '/' ? 'active' : ''} onClick={closeMenu}>{t('nav.home')}</Link></li>
          <li><Link to="/services" className={isActive('/services') ? 'active' : ''} onClick={closeMenu}>{t('nav.services')}</Link></li>
          <li><Link to="/about" className={isActive('/about') ? 'active' : ''} onClick={closeMenu}>{t('nav.about')}</Link></li>
          <li><Link to="/shop" className={isActive('/shop') ? 'active' : ''} onClick={closeMenu}>{t('nav.shop')}</Link></li>
          <li><Link to="/blog" className={isActive('/blog') ? 'active' : ''} onClick={closeMenu}>{t('nav.blog')}</Link></li>
          <li><Link to="/contact" className={isActive('/contact') ? 'active' : ''} onClick={closeMenu}>{t('nav.contact')}</Link></li>
          <li className="nav-mobile-cta">
            <Link to="/appointment" className="btn-book-now-nav btn-book-now-mobile" onClick={closeMenu}>
              {t('common.bookNow')}
            </Link>
          </li>
        </ul>

        <div className="navbar-actions">
          <LanguageSwitcher />
          <Link
            to="/appointment"
            className="btn-book-now-nav"
            onClick={() => setIsOpen(false)}
          >
            {t('common.bookNow')}
          </Link>
        </div>

        <button
          type="button"
          className="menu-icon"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-controls="main-nav-links"
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
        >
          <span className={isOpen ? 'bar open' : 'bar'}></span>
          <span className={isOpen ? 'bar open' : 'bar'}></span>
          <span className={isOpen ? 'bar open' : 'bar'}></span>
        </button>
      </div>
    </nav>
  );
};

export default Navbar;
